import React, { FC, useMemo, useState } from 'react'

import { AddEatableToJournal } from '../DetailCardEatable/components/AddEatableToJournal'
import { FormInputNumber } from '../FormInputNumber'
import { Icon, IconName } from '../Icon'
import { Table } from '../Table'
import { TableNutrition } from '../TableNutrition'
import { DetailCardChart } from '../common/DetailCardChart'

import RichTextPanel from 'Components/RichTextPanel/RichTextPanel'
import { BaseIngredient, IngredientReadData, Nutritions, QuantifiedRecipe, Recipe, nutritionsConfig } from 'Models'
import { Calculator, Formatter } from 'Utils'
import { usePreviousState } from 'Utils/helpers/usePreviousState'

interface Props {
  recipe: Recipe
  quantifiedRecipe?: QuantifiedRecipe
}

type SummaryMacronutrient = 'protein' | 'carbohydrates' | 'fat'

const MACRONUTRIENTS: { name: string; nutrient: SummaryMacronutrient }[] = [
  {
    name: 'Protein',
    nutrient: 'protein'
  },
  {
    name: 'Kohlenh.',
    nutrient: 'carbohydrates'
  },
  {
    name: 'Fett',
    nutrient: 'fat'
  }
]

const DetailCardRecipeContent: FC<Props> = ({ recipe, quantifiedRecipe }: Props) => {
  const [requestedServingsCount, setRequestedServingsCount] = useState<number | undefined>(
    recipe.defaultServingsCount || 1
  )

  const previousServingsCount = usePreviousState(requestedServingsCount)
  const servingsCount = requestedServingsCount || previousServingsCount

  const nutritionsPer100g = useMemo(() => {
    return Calculator.multiplyNutritions(recipe.nutritionsPerPortion as Nutritions, 100 / (recipe.portionAmount || 1))
  }, [recipe.nutritionsPerPortion, recipe.portionAmount])

  const formatIngredientAmount = (ingredient: BaseIngredient): string => {
    const quantity = Calculator.calculateRecipeIngredientQty(recipe, ingredient, servingsCount)

    return Formatter.formatFoodAmount(quantity, ingredient.unit)
  }

  const isAddingToJournalPossible =
    window.location.pathname.includes('rezept-inspiration') ||
    window.location.pathname.includes('favoriten') ||
    window.location.pathname.includes('mein-essen')

  const isFavoriteAmountPreferred = window.location.pathname.includes('favoriten')

  return (
    <>
      <div className="px-5 pt-4 pb-4">
        <h1>{recipe.name}</h1>
      </div>

      {isAddingToJournalPossible && (
        <div className="hiddenInPrint px-5 mb-3">
          <AddEatableToJournal
            recipe={recipe}
            quantifiedEatable={isFavoriteAmountPreferred ? quantifiedRecipe : undefined}
          />
        </div>
      )}

      <details className="mx-5 mb-4">
        <summary className="nutritions-chart-summary detailcard-panel">
          Nährwerte{' '}
          <span className="nutritions-chart-summary-preview">
            (pro Portion):
            {MACRONUTRIENTS.map(({ name, nutrient }) => (
              <span key={nutrient}>
                <span
                  className="table-nutrition-indicator mx-1"
                  style={{ background: nutritionsConfig[nutrient].color }}
                />
                {name} {Formatter.formatNutritionGram(recipe.nutritionsPerPortion[nutrient])}
              </span>
            ))}
          </span>
        </summary>

        <div className="row mt-3">
          <div className="col-12 col-lg-6">
            <TableNutrition nutritionsPer100g={nutritionsPer100g} nutritionsPerPortion={recipe.nutritionsPerPortion} />
          </div>

          <div className="col-12 col-lg-6 d-flex my-3 align-items-center justify-content-center">
            <DetailCardChart nutritionsPerWeight={recipe.nutritions || {}} />
          </div>
        </div>
      </details>

      <div className="row px-5 mt-4 mb-3">
        <div className="col-12">
          <h2>Zutaten</h2>
        </div>
      </div>

      <div className="detailcard-panel detailcard-calculator px-2 mx-5 mb-2 py-1">
        <div className="row justify-content-between">
          <div className="col-auto d-flex align-items-center">
            <Icon name={IconName.utensils} />
            <FormInputNumber
              value={requestedServingsCount}
              onChange={setRequestedServingsCount}
              onChangeDelay={1000}
              maxFractions={1}
              step={0.5}
              hiddenInPrint
            />
            <span className="hiddenOnScreen mr-1">{requestedServingsCount}</span> &nbsp;Portionen
          </div>

          <div className="col-auto d-flex align-items-center">
            <Icon name={IconName.clock} />
            {recipe.preparationTime && <>{Formatter.formatDurationInMinutes(recipe.preparationTime)}</>}
          </div>
        </div>
      </div>

      <div className="px-5 pt-5 pb-3">
        <div className="row">
          <div className="col-12">
            <Table
              rows={
                recipe.ingredients
                  ? recipe.ingredients.map((ingredient: IngredientReadData) => [
                      formatIngredientAmount(ingredient),
                      ingredient.food.name
                    ])
                  : []
              }
              theme="recipe"
            />
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="row">
          <div className="col-12">
            <h2>Zubereitung</h2>
          </div>

          <div className="col-12 mt-1">
            <RichTextPanel text={recipe.description} />
          </div>

          {recipe.hint && (
            <div className="col-12 mt-2">
              <h2>Tipp</h2>

              <p className="mt-3">{recipe.hint}</p>
            </div>
          )}

          {recipe.source && (
            <div className="col-12 mt-3">
              <p>
                <em>Quelle: {recipe.source.name}</em>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default DetailCardRecipeContent
